<template>
    <div class="wrapper">
        <div class="extended-splash" :class="{ show: loadingCss }"></div>
        <img class="app-icon" :class="{ show: loadingCss }" src="/img/icons/android-chrome-512x512.png" alt="">

        <notification-permissions></notification-permissions>

        <!-- INSTALL MODAL -->
        <popup-modal v-if="showInstallPopup" show important>
            <template #content>
                <div class="message">
                    <h3>App installeren</h3>
                    <br>
                    <p>Sla deze webpagina op als een app voor een optimale gebruikerservaring! 🚀</p>
                    <br>
                    <custom-button @click="handleSaveInstallPrompt">Opslaan</custom-button>
                </div>
            </template>
        </popup-modal>

        <!-- UPDATE MODAL -->
        <popup-modal v-else-if="updateExists" show important>
            <template #content>
                <div class="message">
                    <h3>App bijwerken</h3>
                    <br>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="80px" height="80px">
                        <circle transform-origin="center" fill="none" stroke="#505050" stroke-width="10" stroke-linecap="round" stroke-dasharray="200 1000" stroke-dashoffset="0" cx="100" cy="100" r="70">
                            <animateTransform 
                                type="rotate" 
                                attributeName="transform" 
                                calcMode="spline" 
                                dur="1" 
                                values="0;360"
                                keyTimes="0; 1"
                                keySplines="0 0 1 1"
                                repeatCount="indefinite"
                            ></animateTransform>
                            <animate
                                class="animation-tag"
                                attributeName="stroke-dashoffset"
                                from="10"
                                to="-400"
                                dur="2"
                                calcMode="spline"
                                keyTimes="0; 1"
                                keySplines="0 0 1 1"
                                repeatCount="indefinite"
                            />
                            <animate
                                class="animation-tag"
                                attributeName="stroke-dasharray"
                                from="60"
                                to="600"
                                dur="2"
                                calcMode="spline"
                                keyTimes="0; 1"
                                keySplines="0 0 1 1"
                                repeatCount="indefinite"
                            />
                        </circle>
                    </svg>
                    <br>
                    <br>
                    <p>Ik ga voor jou de app updaten.. brb</p>
                </div>
            </template>
        </popup-modal>

        <!-- ROUTER -->
        <router-view v-else />

        <div class="background">
            <div class="container">
                <div class="circles">
                    <div class="circle green"></div>
                    <div class="circle red"></div>
                </div>
            </div>
        </div>

        <!-- KEEP SCREEN ON -->
        <wake-lock></wake-lock>
    </div>
</template>

<script>
import PopupModal from '@/components/PopupModal.vue';
import CustomButton from '@/components/CustomButton.vue';
import WakeLock from '@/components/WakeLock.vue';
import NotificationPermissions from '@/components/NotificationPermissions.vue';

export default {
    name: 'App',
    components: {
        PopupModal,
        CustomButton,
        WakeLock,
        NotificationPermissions,
    },
    data() {
        return {
            registration: null,
            updateExists: false,
            loadingCss: true,
            lastFocusTimestamp: 0,
            deferredPrompt: null,
            showInstallPopup: false,
        }
    },
    methods: {
        async handleCheckForUpdate() {
            if (!document.hidden || document.visibilityState === 'visible') this.$root.$emit('windowfocus');
            if (document.hidden || document.visibilityState === 'hidden') this.$root.$emit('windowunfocus');

            if(!('serviceWorker' in navigator) || document.visibilityState !== 'visible') return;

            const registration = await navigator.serviceWorker.getRegistration()
            if (!registration) return;

            // force a check for updates
            registration.update();

            document.addEventListener('swUpdated', this.updateAvailable);

            navigator.serviceWorker.addEventListener('controllerchange', () => {
                console.log("[SW] New controller active, update needed");
                this.updateNeeded = true;
            });

            navigator.serviceWorker.addEventListener('controllerchange', () => {
                window.location.reload(true);
            });
        },
        updateAvailable() {
            this.updateExists = true;
        },
        handleCheckInstallPrompt() {
            window.addEventListener("beforeinstallprompt", (e) => {
                e.preventDefault();
                this.deferredPrompt = e;
                this.showInstallPopup = true;
            });
        },
        async handleSaveInstallPrompt() {
            if (!this.deferredPrompt) return;

            this.deferredPrompt.prompt();
            await this.deferredPrompt.userChoice;

            this.deferredPrompt = null;
            this.showInstallPopup = false;
        },
        focusReactiveElements(target) {
            this.lastFocusTimestamp = Date.now();
            const elements = document.querySelectorAll('.touch-reactive');
            for(let i = 0; i < elements.length; i++) {
                const element = elements[i];
                if(element.contains(target)) element.classList.add('touch-active');
                else element.classList.remove('touch-active');
            }
        },
        async blurReactiveElements() {
            if(this.lastFocusTimestamp > Date.now() - 100) await new Promise(r => setTimeout(r, Date.now() + 100 - this.lastFocusTimestamp));
            const elements = document.querySelectorAll('.touch-reactive');
            for(let i = 0; i < elements.length; i++) {
                const element = elements[i];
                element.classList.remove('touch-active');
            }
        },
    },
    async created() {
        document.addEventListener('visibilitychange', this.handleCheckForUpdate);

        const isPWA = (
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true
        );
        console.log('Client is in PWA mode: ', isPWA);
        
        document.fonts.ready.then(async (event) => {
            await new Promise(r => setTimeout(r, 100));
            this.loadingCss = false;
        });

        window.addEventListener('load', function() {
            window.history.pushState({}, '');
        });

        window.addEventListener('popstate', (event) => {
            this.$root.$emit('popstate', event);
        });
    },
    async mounted() {
        this.handleCheckForUpdate();
        this.handleCheckInstallPrompt();

        await new Promise(r => setTimeout(r, 500));
        window.addEventListener('pointerdown', (event) => {
            const target = event.target;
            this.focusReactiveElements(target);
            this.$root.$emit('targetElement', event)
        }, false)
        window.addEventListener('touchend', (event) => {
            const target = event.target;
            this.blurReactiveElements();
            this.$root.$emit('clickElement', target)
        }, false)
        window.addEventListener('touchmove', (event) => {
            this.blurReactiveElements();
            this.$root.$emit('targetElementMoved', event)
        }, false)
    }
}
</script>

<style lang="scss">
@import '@/assets/variables.scss';

canvas {
    scale: 1.15;
    transform-origin: center 80%;
}

* {
    font-family: 'Source Sans 3', sans-serif;
    letter-spacing: .5px;
    overscroll-behavior: none;
}

img.emoji {
    height: 1em;
    width: 1em;
    margin: 0 .05em 0 .1em;
    vertical-align: -0.1em;
}

p, a {
    font-size: 15px;
    line-height: 21px;
}

p, a, h1, h2, h3 {
    margin: 0;
}
html, body {
    width: 100%;
    height: 100%;
    margin: 0px;
    padding: 0px;
    user-select: none;
    touch-action: pan-y;
    -webkit-tap-highlight-color: transparent;
}
body {
    box-sizing: border-box;
    overflow: hidden;
    overscroll-behavior: none;
    background: #152112;
}

.flex {
    display: flex;
}
.align-center {
    align-items: center;
}
.justify-center {
    justify-content: center;
}
.flex-column {
    display: flex;
    flex-direction: column;
}

.flex-column-gap {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.wrapper {
    --absolute-width: min(100vw, 500px);
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: var(--absolute-width);
    margin-inline: auto;

    > .page {
        flex-grow: 1;
    }

    .background {
        position: fixed;
        width: 100dvw;
        height: 100dvh;
        left: 0;
        overflow: hidden;
        pointer-events: none;
        z-index: -1;

        .container {
            --circle-size: 80vw;
            position: fixed;
            left: calc(var(--circle-size) * -1);
            right: calc(var(--circle-size) * -1);
            top: calc(var(--circle-size) * -1);
            bottom: calc(var(--circle-size) * -1);
            display: grid;
            place-items: center;
            background-color: #1b2a1b;
            z-index: -1;
            opacity: .9;
            filter: blur(150px);
            -webkit-filter: blur(150px);

            .circles {
                display: flex;
                animation: rotate 40s linear infinite, spacing 20s ease-in-out infinite;
                -webkit-animation: rotate 40s linear infinite, spacing 20s ease-in-out infinite;
    
                .circle {
                    width: var(--circle-size);
                    height: calc(var(--circle-size) * 2);
                    border-radius: 50%;
        
                    &.red {
                        background-color: #18833a80;
                        // background-color: #a34a1d;
                    }
                    &.green {
                        background-color: #40771280;
                    }
                }
            }
        }
        

    }
}

.extended-splash {
    position: fixed;
    background: #1F211F;
    inset: 0;
    z-index: 205;
    pointer-events: none;
    opacity: 0;
    transition: 700ms ease-in-out;
    
    &.show {
        opacity: 1;
    }

}

.app-icon {
    position: fixed;
    top: 50%;
    left: 50%;
    translate: -50% -50%;
    width: calc(var(--absolute-width) / 100 * 75);
    height: calc(var(--absolute-width) / 100 * 75);
    opacity: 0;
    pointer-events: none;
    opacity: 0;
    z-index: 204;

    &.show {
        opacity: 1;
    }
}

@keyframes rotate {
    to { rotate: 360deg; }
}
@keyframes spacing {
    0%, 100% { gap: 20vw }
    50% { gap: 40vw; }
}

*:focus-visible {
    outline: 2px solid $weed-green;
}

::-webkit-scrollbar {
  display: none;
}

</style>
